/**
 * SereneBackground Component
 *
 * A reusable background animation component that provides subtle, calming visual
 * effects for the Serene Zone. Designed for minimal CPU usage with CSS-only
 * animations where possible.
 *
 * Background Types:
 * - gradient (default): Pure CSS gradient, lowest CPU usage
 * - particles: Gradient with subtle floating particles (using MagicUI Particles)
 * - wave: Gradient with gentle ripple effect (using MagicUI Ripple)
 *
 * Accessibility:
 * - Respects prefers-reduced-motion preference
 * - Reduces/disables animations when useReducedMotion is true
 * - Uses serene color palette for calming visual experience
 *
 * @see docs/taskmaster/reports/holding/SERENE_BREATHING_DESIGN_SPEC.md
 */

'use client';

import { useReducedMotion } from 'framer-motion';
import { useTheme } from 'next-themes';
import { cn } from '@/utils/cn';
import { Particles } from '@/components/ui/particles';
import { Ripple } from '@/components/ui/ripple';

/**
 * Background type options for SereneBackground
 */
export type SereneBackgroundType = 'gradient' | 'particles' | 'wave';

/**
 * Custom color configuration for the background
 */
export interface SereneBackgroundColors {
  /** Primary gradient color (HSL format, e.g., "175 28% 85%") */
  primary?: string;
  /** Secondary gradient color (HSL format) */
  secondary?: string;
  /** Tertiary gradient color (HSL format) */
  tertiary?: string;
}

/**
 * Props for the SereneBackground component
 */
export interface SereneBackgroundProps {
  /** Type of background animation */
  type?: SereneBackgroundType;
  /** Custom colors for the gradient (HSL format) */
  colors?: SereneBackgroundColors;
  /** Optional background image URL for overlay */
  imageUrl?: string;
  /** Opacity for the background image (0-1) */
  imageOpacity?: number;
  /** Additional CSS classes */
  className?: string;
}

/**
 * Default serene colors from CSS custom properties
 * These reference the --serene-* tokens defined in index.css
 * Format: CSS variable reference for use with hsl()
 */
/**
 * Particle configuration for the MagicUI Particles component
 * Uses soft teal/sage colors that match the serene palette
 */
const PARTICLE_CONFIG = {
  quantity: 60, // Subtle effect with 50-80 particles
  size: 0.6, // Small particles for gentle appearance
  staticity: 80, // Higher staticity for calmer movement
  ease: 80, // Smoother easing for relaxed feel
} as const;

/**
 * Theme-aware particle colors
 * Soft teal/sage that matches the serene palette
 */
const PARTICLE_COLORS = {
  light: '#5f9ea0', // Cadet blue/soft teal for light mode
  dark: '#4a7c7e', // Darker teal for dark mode
} as const;

/**
 * Ripple configuration for calming wave effect
 * Configured for slow, subtle, relaxing animation
 */
const RIPPLE_CONFIG = {
  mainCircleSize: 400, // Larger initial circle for more prominent wave effect
  mainCircleOpacity: 0.24, // Increased for visibility testing (was 0.08)
  numCircles: 5, // Fewer circles for gentle appearance
} as const;

/**
 * SereneBackground - Calming background animation component
 *
 * Provides subtle visual effects designed for relaxation contexts.
 * Uses CSS-only animations for minimal CPU usage.
 */
export function SereneBackground({
  type = 'gradient',
  colors,
  imageUrl,
  imageOpacity = 0.3,
  className,
}: SereneBackgroundProps) {
  const shouldReduceMotion = useReducedMotion();
  const { resolvedTheme } = useTheme();

  // Determine particle color based on current theme
  const particleColor =
    resolvedTheme === 'dark' ? PARTICLE_COLORS.dark : PARTICLE_COLORS.light;

  // Use CSS custom properties for colors - allows light/dark mode to work automatically
  // Custom colors override the defaults if provided
  const useCustomColors = !!(
    colors?.primary ||
    colors?.secondary ||
    colors?.tertiary
  );

  return (
    <div
      className={cn(
        // Absolute positioning to fill container
        'absolute inset-0 overflow-hidden',
        // Prevent pointer events from blocking content
        'pointer-events-none',
        // Z-index below content
        'z-0',
        className
      )}
      aria-hidden="true"
    >
      {/* Base Gradient Layer - Uses CSS custom properties for automatic dark mode */}
      <div
        className={cn(
          'absolute inset-0',
          'bg-gradient-to-br',
          // Use CSS variables - automatically adapts to light/dark mode
          !useCustomColors && 'from-[hsl(var(--serene-gradient-start))]',
          !useCustomColors && 'via-[hsl(var(--serene-gradient-mid))]',
          !useCustomColors && 'to-[hsl(var(--serene-gradient-end))]'
        )}
        style={
          useCustomColors
            ? {
                background: `linear-gradient(to bottom right,
            hsl(${colors?.primary || '175 28% 85%'}),
            hsl(${colors?.secondary || '160 22% 80%'}),
            hsl(${colors?.tertiary || '145 18% 88%'}))`,
              }
            : undefined
        }
      />


      {/* Particles Layer - Only rendered for 'particles' type */}
      {/* Respects reduced motion preference by not rendering */}
      {type === 'particles' && !shouldReduceMotion && (
        <Particles
          className="absolute inset-0"
          quantity={PARTICLE_CONFIG.quantity}
          size={PARTICLE_CONFIG.size}
          staticity={PARTICLE_CONFIG.staticity}
          ease={PARTICLE_CONFIG.ease}
          color={particleColor}
          refresh={false}
        />
      )}

      {/* Wave/Ripple Layer - Only rendered for 'wave' type */}
      {/* Uses MagicUI Ripple with calming configuration */}
      {/* Positioned at bottom so only top half of ripples are visible */}
      {/* Respects reduced motion preference by not rendering */}
      {type === 'wave' && !shouldReduceMotion && (
        <Ripple
          mainCircleSize={RIPPLE_CONFIG.mainCircleSize}
          mainCircleOpacity={RIPPLE_CONFIG.mainCircleOpacity}
          numCircles={RIPPLE_CONFIG.numCircles}
          position="bottom"
          className="serene-ripple"
        />
      )}

      {/* Optional Image Overlay */}
      {imageUrl && (
        <div
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: `url(${imageUrl})`,
            opacity: imageOpacity,
          }}
        />
      )}
    </div>
  );
}
